import { DocumentNode } from 'graphql';
import { ShoppingBag, Tag, Trash } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GetCouponQuery as GetCoupon, GetCouponsQuery as GetCoupons } from '__generated__/graphql';

import { Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../../common/helpers';
import { useCouponStatus } from './CouponStatusIndicator';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivateCouponMutation from './ActivateCouponMutation';
import CardPreview from '../CardPreview';
import DeactivateCouponMutation from './DeactivateCouponMutation';
import DeleteCouponMutation from './DeleteCouponMutation';
import DiscountValue from '../../../../common/Common/DiscountValue';
import EditCouponForm from './EditCouponForm';
import GetCouponsQuery from './GetCouponsQuery';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../../common/useProject';

type Coupon = GetCoupons['project']['event']['coupons'][0];
type Event = GetCoupons['project']['event'];

interface CouponListProps {
  event: Event;
  onResetFilters: () => void;
  activeCouponId: string;
  couponData?: GetCoupon;
  refetchQueries?: DocumentNode[];
}

const CouponList = ({
  event, onResetFilters, activeCouponId, couponData, refetchQueries = [GetCouponsQuery],
}: CouponListProps) => {
  const project = useProject();
  const { t } = useTranslation();

  const hasFilteredCoupons = event.coupons.length > 0;

  return (
    <UI.Div>
      {hasFilteredCoupons ? (
        <UI.GridContainer sc={{ gutter: 1 / 3 }}>
          {event.coupons.map((coupon, index) => (
            <UI.CollapsibleNavCard
              id={`CouponCard_${coupon.id}`}
              key={index}
              to={`/${project.id}/events/${event.id}/coupons/${activeCouponId !== coupon.id ? coupon.id : ''}`}
              open={coupon.id === activeCouponId}
              muted={!!activeCouponId && coupon.id !== activeCouponId}
              preview={(
                <CouponPreview
                  coupon={coupon}
                  event={event}
                  refetchQueries={refetchQueries}
                />
              )}
              body={couponData
                ? <EditCouponForm coupon={couponData.coupon} event={couponData.event} />
                : <UI.PageLoader />}
            />
          ))}
        </UI.GridContainer>
      ) : (
        <UI.Card
          sc={{ padding: 8, textAlign: 'center' }}
        >
          <UI.Div sc={{ color: 'secondary', mb: 2 }}>
            <Tag strokeWidth={0.5} size={60} />
          </UI.Div>
          <UI.Span>{t('no_coupons_match_filters')}</UI.Span>
          {' '}
          <UI.A onClick={onResetFilters}>{t('reset_filters')}</UI.A>
        </UI.Card>
      )}
    </UI.Div>
  );
};

interface CouponPreviewProps {
  coupon: Coupon;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const CouponPreview = ({ coupon, event, refetchQueries }: CouponPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const history = useHistory();
  const project = useProject();

  const [activateCoupon] = useMutation(ActivateCouponMutation, {
    variables: {
      id: coupon.id,
    },
    optimisticResponse: {
      activateCoupon: {
        id: coupon.id,
        active: true,
        __typename: 'Coupon',
      },
    },
    onCompleted: () => notifier.success(t('notifications.coupon_activated')),
  });

  const [deactivateCoupon] = useMutation(DeactivateCouponMutation, {
    variables: {
      id: coupon.id,
    },
    optimisticResponse: {
      deactivateCoupon: {
        id: coupon.id,
        active: false,
        __typename: 'Coupon',
      },
    },
    onCompleted: () => notifier.success(t('notifications.coupon_deactivated')),
  });

  const toggleStatus = () => {
    if (coupon.active) {
      deactivateCoupon();
    } else {
      activateCoupon();
    }
  };

  const [deleteCoupon, { loading: deleting }] = useMutation(
    DeleteCouponMutation, {
      variables: {
        id: coupon.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.coupon_deleted'));

        history.push(`/${project.id}/events/${event.id}/coupons`);
      },
      onError: () => notifier.error(t('something_went_wrong')),
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: coupon.prefix }),
    onConfirm: () => deleteCoupon(),
  }, [coupon]);

  const title = `${coupon.prefix}${coupon.codes_count > 0 ? '········' : ''}`;
  const { message } = useCouponStatus(coupon);

  return (
    <CardPreview
      title={title}
      firstColumn={<DiscountValue discount={coupon} />}
      secondColumn={(
        <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
          <UI.Link
            to={`/${project.id}/participants/orders?${encodeUrl({
              event: event.id,
              coupons: [coupon.id],
            })}`}
            sc={{ secondary: true }}
          >
            <UI.Icon>
              <ShoppingBag />
            </UI.Icon>
            {' '}
            {` ${formatInteger(coupon.uses)} / ${coupon.max_uses !== null ? formatInteger(coupon.max_uses) : '∞'}`}
          </UI.Link>
          <UI.Tooltip sc={{ alignH: 'center' }}>
            {t('times_used')}
          </UI.Tooltip>
        </UI.TooltipContainer>
      )}
      status={message}
      active={coupon.active}
      onToggle={toggleStatus}
      options={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={!coupon.deletable || deleting}
        />
      )}
    />
  );
};

export default CouponList;
